import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, del } from '@truefit/http-utils';

import type {
  WscStream,
  WscStreamCreatePayload,
  TrackedEntitiesResponse,
} from '../types';

/**
 * Query key conventions:
 *   ['wsc-streams', 'game', gameId] — list for one game
 *   ['wsc-streams', 'tracked-entities', gameId] — entities for the add form
 *
 * Polling is conditional on the page being visible (react-query default) plus
 * a 30s base interval. Per-row polling stops once the row reaches a terminal
 * status — the parent invalidates anyway when rows transition.
 */

export const wscStreamsQueryKeys = {
  game: (gameId: string) => ['wsc-streams', 'game', gameId] as const,
  trackedEntities: (gameId: string) => ['wsc-streams', 'tracked-entities', gameId] as const,
  gameCameras: (gameId: string) => ['game-cameras', gameId] as const,
};

interface GameCameraSimple {
  id: number;
  uniqueId: string;
  displayName: string | null;
  camIndex: number;
}

export function useGameCameras(gameId: string, enabled = true) {
  return useQuery({
    queryKey: wscStreamsQueryKeys.gameCameras(gameId),
    queryFn: async () => {
      const { data } = await get<GameCameraSimple[]>(`/v1.0/games/${gameId}/cameras`);
      return data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

const STATUS_POLL_MS = 30 * 1000;

export function useGameWscStreams(gameId: string, enabled = true) {
  return useQuery({
    queryKey: wscStreamsQueryKeys.game(gameId),
    queryFn: async () => {
      const { data } = await get<WscStream[]>(`/v1.0/games/${gameId}/streams`);
      return data;
    },
    enabled,
    refetchInterval: STATUS_POLL_MS,
  });
}

export function useGameTrackedEntities(gameId: string, enabled = true) {
  return useQuery({
    queryKey: wscStreamsQueryKeys.trackedEntities(gameId),
    queryFn: async () => {
      const { data } = await get<TrackedEntitiesResponse>(
        `/v1.0/games/${gameId}/tracked-entities`,
      );
      return data;
    },
    enabled,
    // Tracked entities don't change often (registrations are loaded once and
    // cached server-side for 7 days). Don't poll.
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateWscStream(gameId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: WscStreamCreatePayload): Promise<WscStream> => {
      const { data } = await post<WscStream>(
        `/v1.0/games/${gameId}/streams`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wscStreamsQueryKeys.game(gameId) });
    },
  });
}

/**
 * Launches a stream immediately instead of waiting for the scheduler's
 * warmup-window sweep. Only PENDING / SCHEDULED rows are accepted; anything
 * else comes back as a 409.
 */
export function useLaunchWscStream(gameId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // Path uses streamId (numeric), not gameId — but we invalidate by gameId.
    mutationFn: async (streamId: number): Promise<{ streamId: number }> => {
      await post(`/v1.0/streams/${streamId}/launch`, {});
      return { streamId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wscStreamsQueryKeys.game(gameId) });
    },
  });
}

export function useDeleteWscStream(gameId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // Path uses streamId (numeric), not gameId — but we invalidate by gameId.
    mutationFn: async (streamId: number): Promise<{ streamId: number }> => {
      await del(`/v1.0/streams/${streamId}`);
      return { streamId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wscStreamsQueryKeys.game(gameId) });
    },
  });
}
